import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatNumber, localNow, type User } from '@jungles/shared';
import { api, loadToken } from '../lib/api';

type Period = { id: string; liftedKg: number; eatenKcal: number; liftedEquivalent: string; eatenEquivalent: string };
type Statistics = { today: string; periods: Period[] };
const periods = [['week', 'За неделю'], ['month', 'За месяц'], ['all', 'За всё время']] as const;
const pageStride = (node: HTMLDivElement) => node.clientWidth + (parseFloat(getComputedStyle(node).columnGap) || 0);

export function HomeStatistics({ user }: { user: User }) {
  const [selected, setSelected] = useState(0);
  const [data, setData] = useState<Statistics | null>(null);
  const [failed, setFailed] = useState(false);
  const [date, setDate] = useState(() => localNow(user.timezone).date);
  const [nonce, setNonce] = useState(0);
  const pager = useRef<HTMLDivElement>(null);
  const current = useRef(selected); current.current = selected;
  const userScroll = useRef(false);
  const touching = useRef(false);
  const scrollIdle = useRef<ReturnType<typeof setTimeout>>();
  const finishScroll = () => {
    clearTimeout(scrollIdle.current);
    userScroll.current = false;
  };
  const settleScroll = () => {
    clearTimeout(scrollIdle.current);
    if (touching.current) return;
    scrollIdle.current = setTimeout(finishScroll, 150);
  };
  const go = (index: number) => {
    const next = Math.max(0, Math.min(2, index));
    finishScroll();
    current.current = next; setSelected(next);
    if (pager.current) pager.current.scrollLeft = next * pageStride(pager.current);
  };
  useLayoutEffect(() => {
    const node = pager.current!;
    const resize = new ResizeObserver(() => {
      // Layout scroll events may arrive before this observer, even after width
      // has recovered from zero. Only an input gesture may change selection.
      finishScroll();
      if (node.clientWidth > 0) node.scrollLeft = current.current * pageStride(node);
    });
    node.scrollLeft = current.current * pageStride(node); resize.observe(node);
    const end = () => { if (!touching.current) finishScroll(); };
    node.addEventListener('scrollend', end);
    return () => { resize.disconnect(); node.removeEventListener('scrollend', end); clearTimeout(scrollIdle.current); };
  }, []);
  useEffect(() => {
    const tick = () => setDate(localNow(user.timezone).date);
    const refresh = () => { if (document.visibilityState === 'visible') { tick(); setNonce(n => n + 1); } };
    tick(); const timer = setInterval(tick, 1000);
    window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [user.timezone]);
  useEffect(() => {
    let active = true; const controller = new AbortController(); const token = loadToken();
    setFailed(false);
    api.get<Statistics>('/api/home/statistics', controller.signal).then(value => {
      if (active && token === loadToken()) setData(value.today === date ? value : null);
    }).catch(() => { if (active && token === loadToken()) { setData(null); setFailed(true); } });
    return () => { active = false; controller.abort(); };
  }, [date, nonce, user.id]);
  const valid = data?.today === date ? data : null;
  return <section id="statistics" aria-label="Статистика">
    <div className="secl">Статистика</div>
    <div id="period-title" aria-live="polite">{periods[selected]![1]}</div>
    <div className="pager" ref={pager} tabIndex={0} aria-label="Статистика по периодам. Используйте стрелки"
      onTouchStart={() => { touching.current = true; clearTimeout(scrollIdle.current); userScroll.current = true; }}
      onTouchEnd={() => { touching.current = false; settleScroll(); }}
      onTouchCancel={() => { touching.current = false; settleScroll(); }}
      onPointerDown={() => { clearTimeout(scrollIdle.current); userScroll.current = true; }}
      onPointerUp={settleScroll} onPointerCancel={settleScroll}
      onWheel={event => { if (event.deltaX !== 0) { userScroll.current = true; settleScroll(); } }}
      onScroll={event => {
        const el = event.currentTarget;
        if (el.clientWidth <= 0) { finishScroll(); return; }
        if (!userScroll.current) { el.scrollLeft = current.current * pageStride(el); return; }
        const next = Math.max(0, Math.min(2, Math.round(el.scrollLeft / pageStride(el))));
        current.current = next; setSelected(next); settleScroll();
      }}
      onKeyDown={event => { const index = { ArrowLeft: selected - 1, ArrowRight: selected + 1, Home: 0, End: 2 }[event.key]; if (index !== undefined) { event.preventDefault(); go(index); } }}>
      {periods.map(([id, label], index) => {
        const value = valid?.periods.find(p => p.id === id);
        return <div className="stats-page" role="group" aria-label={label} aria-hidden={index !== selected} key={id}>
          <div className="tiles">
            <div className="tile"><img src="/assets/ui/elephant.webp" alt="" /><small>Поднято</small>
              <b>{value && Number.isFinite(value.liftedKg) ? formatNumber(value.liftedKg) : '—'} кг</b><span>{value?.liftedEquivalent ?? 'Нет данных'}</span></div>
            <div className="tile"><img src="/assets/ui/shark.webp" alt="" /><small>Съедено</small>
              <b>{value && Number.isFinite(value.eatenKcal) ? formatNumber(value.eatenKcal) : '—'} ккал</b><span>{value?.eatenEquivalent ?? 'Нет данных'}</span></div>
          </div>
        </div>;
      })}
    </div>
    <div className="stats-accessible-periods" aria-label="Страницы статистики">{periods.map(([id, label], index) =>
      <button type="button" aria-label={label} aria-pressed={index === selected} onClick={() => go(index)} key={id}>{label}</button>)}</div>
    {failed ? <button className="back" type="button" onClick={() => setNonce(n => n + 1)}>Не удалось загрузить статистику · Повторить</button> : null}
  </section>;
}
